import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiDownload,
  FiEdit3,
  FiEye,
  FiGitPullRequest,
  FiPlus,
  FiSave,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import ManualSaleDialog, { canCreateManualSale } from "../components/ManualSaleDialog";

const POS_SALES_COLUMNS = [
  { key: "outlet", label: "Outlets", sub: "Outlet", width: 170 },
  { key: "check_number", label: "Check#", sub: "Ticket", width: 115 },
  { key: "item_name", label: "Item Name", sub: "Product", width: 240 },
  { key: "plu", label: "PLU", sub: "Code", width: 135 },
  { key: "date", label: "Date", sub: "Sale date", width: 110 },
  { key: "time", label: "Time", sub: "Sale time", width: 95 },
  { key: "quantity", label: "Quantity", sub: "Qty", width: 90 },
  { key: "price_per_item", label: "Price per item", sub: "Line amount", width: 120 },
  { key: "service_charge", label: "SC", sub: "Service", width: 90 },
  { key: "discount_type", label: "Discount type", sub: "Discount", width: 145 },
  { key: "discount_amount", label: "Discount Amount", sub: "Amount", width: 130 },
  { key: "check_total", label: "Check Total", sub: "Ticket total", width: 130 },
  { key: "payment_method", label: "Payment Method", sub: "Payment", width: 220 },
  { key: "room_number", label: "Room#", sub: "Room", width: 95 },
  { key: "revenue_type", label: "Revenue type", sub: "Revenue", width: 135 },
  { key: "operator", label: "Operator", sub: "Operator", width: 150 },
  { key: "cashier", label: "Cashier", sub: "Cashier", width: 150 },
  { key: "guests", label: "Guests", sub: "Pax", width: 90 },
  { key: "dining_time", label: "Dining Time", sub: "Service", width: 125 },
  { key: "status", label: "Status", sub: "State", width: 115 },
];

const PAGE_SIZE_OPTIONS = [50, 100, 200];

const PAYMENT_CATEGORY_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "credit_card", label: "Credit Card" },
  { value: "room_charge", label: "Room Charge" },
  { value: "other", label: "Others" },
];

const TICKET_CHECK_FIELDS = [
  { key: "guests", label: "Pax / Covers" },
  { key: "table_number", label: "Table" },
  { key: "room_number", label: "Room" },
  { key: "revenue_type", label: "Revenue type" },
  { key: "cashier_name", label: "Cashier" },
  { key: "dining_time", label: "Dining time" },
  { key: "status", label: "Status" },
];

const TICKET_LINE_FIELDS = [
  { key: "item_name", label: "Item" },
  { key: "plu", label: "PLU" },
  { key: "line_time", label: "Time" },
  { key: "quantity", label: "Qty", numeric: true },
  { key: "line_amount", label: "Amount", numeric: true },
  { key: "service_charge_amount", label: "SC", numeric: true },
  { key: "discount_type", label: "Discount type" },
  { key: "discount_amount", label: "Discount", numeric: true },
  { key: "operator_name", label: "Operator" },
];

const TICKET_PAYMENT_FIELDS = [
  { key: "payment_method", label: "Method" },
  { key: "amount", label: "Amount", numeric: true },
];

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function formatMoney(value) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(value || 0));
}

function displayValue(row, column, showOriginal = false) {
  const value = showOriginal
    ? row?.raw_json?.[column.label] ?? row?.display?.[column.key]
    : row?.display?.[column.key] ?? row?.raw_json?.[column.label];
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function inputValue(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function numericValue(value) {
  if (value === null || value === undefined || value === "") return 0;
  const normalized = String(value).replace(/[^\d,.-]/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseFileNameFromDisposition(disposition, fallback = "pos_import_template.xlsx") {
  if (!disposition) return fallback;
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1].replace(/"/g, ""));
  const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] || fallback;
}

function normalizeCompareValue(value, numeric = false) {
  if (value === null || value === undefined || value === "") return "";
  if (!numeric) return String(value).trim();
  const number = numericValue(value);
  return Number.isFinite(number) ? String(Number(number.toFixed(4))) : "";
}

function formatDiffValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function computeDraftTotals(draft) {
  const lineTotals = (draft?.lines || []).reduce(
    (acc, line) => {
      if (line._delete) return acc;
      const amount = numericValue(line.line_amount);
      const service = numericValue(line.service_charge_amount);
      const discount = numericValue(line.discount_amount);
      acc.lines += 1;
      acc.lineTotal += amount;
      acc.service += service;
      acc.discount += discount;
      acc.total += amount + service;
      return acc;
    },
    { lines: 0, lineTotal: 0, service: 0, discount: 0, total: 0, payments: 0, paymentTotal: 0 }
  );

  return (draft?.payments || []).reduce((acc, payment) => {
    if (payment._delete) return acc;
    acc.payments += 1;
    acc.paymentTotal += numericValue(payment.amount);
    return acc;
  }, lineTotals);
}

function buildBlankTicketLine(check = {}) {
  return {
    local_id: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    id: null,
    pos_product_id: "",
    line_time: inputValue(check.last_line_time || check.first_line_time),
    item_name: "",
    plu: "",
    quantity: "1",
    line_amount: "0",
    service_charge_amount: "0",
    discount_type: "",
    discount_amount: "0",
    operator_name: "",
    line_origin: "manual",
    edit_status: "added",
    _delete: false,
  };
}

function buildBlankTicketPayment() {
  return {
    local_id: `new-payment-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    id: null,
    payment_method: "",
    amount: "0",
    raw_payment_text: "",
    category: "other",
    category_label: "Others",
    _delete: false,
  };
}

function buildTicketDraft(payload) {
  const check = payload?.check || {};
  return {
    note: "",
    check: {
      table_number: inputValue(check.table_number),
      room_number: inputValue(check.room_number),
      revenue_type: inputValue(check.revenue_type),
      cashier_name: inputValue(check.cashier_name),
      guests: inputValue(check.guests),
      dining_time: inputValue(check.dining_time),
      status: inputValue(check.status),
    },
    lines: (payload?.lines || []).map((line) => ({
      local_id: line.id ? `line-${line.id}` : buildBlankTicketLine(check).local_id,
      id: line.id || null,
      pos_product_id: inputValue(line.pos_product_id || line.product?.id),
      line_time: inputValue(line.line_time),
      item_name: inputValue(line.item_name),
      plu: inputValue(line.plu),
      quantity: inputValue(line.quantity),
      line_amount: inputValue(line.line_amount),
      service_charge_amount: inputValue(line.service_charge_amount),
      discount_type: inputValue(line.discount_type),
      discount_amount: inputValue(line.discount_amount),
      operator_name: inputValue(line.operator_name),
      line_origin: line.line_origin || "imported",
      edit_status: line.edit_status || "original",
      raw_row_json: line.raw_row_json || null,
      _delete: Boolean(line.is_removed),
    })),
    payments: (payload?.payments || []).map((payment) => ({
      local_id: payment.id ? `payment-${payment.id}` : buildBlankTicketPayment().local_id,
      id: payment.id || null,
      pos_payment_method_id: payment.pos_payment_method_id || null,
      payment_method: inputValue(payment.payment_method),
      amount: inputValue(payment.amount),
      raw_payment_text: inputValue(payment.raw_payment_text),
      category: payment.category || payment.method?.category || "other",
      category_label: payment.category_label || payment.method?.category_label || "Others",
      _delete: false,
    })),
  };
}

function getSourceValue(ticket, key) {
  const value = ticket?.source_snapshot?.check?.[key];
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function getSourceLineLabel(line) {
  const raw = line?.raw_row_json || {};
  if (raw.manual_entry) return "Manual line";
  const item = raw["Item Name"] || line.item_name || "-";
  const qty = raw.Quantity || line.quantity || "-";
  const amount = raw["Price per item"] || line.line_amount || "-";
  return `${item} / Qty ${qty} / ${amount}`;
}

function getSourcePaymentLabel(payment) {
  if (payment?.raw_payment_text) return payment.raw_payment_text;
  const method = payment?.payment_method || "-";
  const amount = payment?.amount ?? "-";
  return `${method} / ${amount}`;
}

function lineLabel(line) {
  return line?.item_name || line?.raw_row_json?.["Item Name"] || line?.plu || `Line ${line?.id || ""}`.trim();
}

function paymentLabel(payment, index = 0) {
  return payment?.payment_method || `Payment ${index + 1}`;
}

function categoryLabel(value) {
  return PAYMENT_CATEGORY_OPTIONS.find((option) => option.value === value)?.label || "Others";
}

function productOptionLabel(product) {
  const plu = inputValue(product?.plu);
  const name = inputValue(product?.name || product?.item_name);
  if (plu && name) return `${plu} - ${name}`;
  return name || plu || "Unknown product";
}

function buildTicketDifferences(sourceSnapshot, currentTicket) {
  const differences = [];
  const sourceCheck = sourceSnapshot?.check || {};
  const currentCheck = currentTicket?.check || {};

  const checkChanges = TICKET_CHECK_FIELDS.map((field) => ({
    label: field.label,
    before: sourceCheck[field.key],
    after: currentCheck[field.key],
    changed:
      normalizeCompareValue(sourceCheck[field.key], field.numeric) !==
      normalizeCompareValue(currentCheck[field.key], field.numeric),
  })).filter((change) => change.changed);

  if (checkChanges.length) {
    differences.push({
      type: "check",
      title: "Ticket fields",
      changes: checkChanges,
    });
  }

  const sourceLines = sourceSnapshot?.lines || [];
  const currentLines = currentTicket?.lines || [];
  const sourceById = new Map(sourceLines.filter((line) => line.id).map((line) => [String(line.id), line]));
  const currentById = new Map(currentLines.filter((line) => line.id).map((line) => [String(line.id), line]));

  currentLines.forEach((line) => {
    const key = line.id ? String(line.id) : "";
    const sourceLine = key ? sourceById.get(key) : null;

    if (!sourceLine && !line.is_removed) {
      differences.push({
        type: "line_added",
        title: `Added line: ${lineLabel(line)}`,
        changes: TICKET_LINE_FIELDS.map((field) => ({
          label: field.label,
          before: null,
          after: line[field.key],
        })),
      });
      return;
    }

    if (sourceLine && line.is_removed) {
      differences.push({
        type: "line_removed",
        title: `Removed line: ${lineLabel(sourceLine)}`,
        changes: TICKET_LINE_FIELDS.map((field) => ({
          label: field.label,
          before: sourceLine[field.key],
          after: null,
        })),
      });
      return;
    }

    if (!sourceLine) return;

    const changes = TICKET_LINE_FIELDS.map((field) => ({
      label: field.label,
      before: sourceLine[field.key],
      after: line[field.key],
      changed:
        normalizeCompareValue(sourceLine[field.key], field.numeric) !==
        normalizeCompareValue(line[field.key], field.numeric),
    })).filter((change) => change.changed);

    if (changes.length) {
      differences.push({
        type: "line_edited",
        title: `Edited line: ${lineLabel(line)}`,
        changes,
      });
    }
  });

  sourceLines.forEach((line) => {
    if (!line.id || currentById.has(String(line.id))) return;
    differences.push({
      type: "line_removed",
      title: `Removed line: ${lineLabel(line)}`,
      changes: TICKET_LINE_FIELDS.map((field) => ({
        label: field.label,
        before: line[field.key],
        after: null,
      })),
    });
  });

  const sourcePayments = sourceSnapshot?.payments || [];
  const currentPayments = currentTicket?.payments || [];
  const maxPaymentLength = Math.max(sourcePayments.length, currentPayments.length);

  for (let index = 0; index < maxPaymentLength; index += 1) {
    const sourcePayment = sourcePayments[index] || null;
    const currentPayment = currentPayments[index] || null;

    if (!sourcePayment && currentPayment) {
      differences.push({
        type: "payment_added",
        title: `Added payment: ${paymentLabel(currentPayment, index)}`,
        changes: TICKET_PAYMENT_FIELDS.map((field) => ({
          label: field.label,
          before: null,
          after: currentPayment[field.key],
        })),
      });
      continue;
    }

    if (sourcePayment && !currentPayment) {
      differences.push({
        type: "payment_removed",
        title: `Removed payment: ${paymentLabel(sourcePayment, index)}`,
        changes: TICKET_PAYMENT_FIELDS.map((field) => ({
          label: field.label,
          before: sourcePayment[field.key],
          after: null,
        })),
      });
      continue;
    }

    if (!sourcePayment || !currentPayment) continue;

    const changes = TICKET_PAYMENT_FIELDS.map((field) => ({
      label: field.label,
      before: sourcePayment[field.key],
      after: currentPayment[field.key],
      changed:
        normalizeCompareValue(sourcePayment[field.key], field.numeric) !==
        normalizeCompareValue(currentPayment[field.key], field.numeric),
    })).filter((change) => change.changed);

    if (changes.length) {
      differences.push({
        type: "payment_edited",
        title: `Edited payment: ${paymentLabel(currentPayment, index)}`,
        changes,
      });
    }
  }

  return differences;
}

export default function Sales() {
  const [searchParams] = useSearchParams();
  const { languageId } = usePmsTheme();
  const manualSpanish = languageId === "es";
  const [manualSaleOpen, setManualSaleOpen] = useState(false);
  const [createdSale, setCreatedSale] = useState(null);
  const [manualReload, setManualReload] = useState(0);
  const rowRequest = useRef(0);
  useEffect(() => {
    const changed = () => { rowRequest.current += 1; setCreatedSale(null); };
    window.addEventListener("palantir-session-updated", changed);
    return () => { rowRequest.current += 1; window.removeEventListener("palantir-session-updated", changed); };
  }, []);
  const businessUnitId = getBusinessUnitId(searchParams);
  const fileInputRef = useRef(null);
  const filterPopoverRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [showOriginalSales, setShowOriginalSales] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterDraft, setFilterDraft] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [lastImportMessage, setLastImportMessage] = useState("");
  const [editingCheckId, setEditingCheckId] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [ticketDraft, setTicketDraft] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketSaving, setTicketSaving] = useState(false);
  const [ticketError, setTicketError] = useState("");
  const [ticketMode, setTicketMode] = useState("edit");

  const totalPages = useMemo(
    () => (Number(totalRows) > 0 ? Math.ceil(Number(totalRows) / pageSize) : 1),
    [pageSize, totalRows]
  );
  const pageStart = totalRows > 0 ? (page - 1) * pageSize + 1 : 0;
  const pageEnd = totalRows > 0 ? Math.min(Number(totalRows), (page - 1) * pageSize + rows.length) : 0;

  const loadSummary = useCallback(async () => {
    const res = await axios.get("/api/pos/dashboard", { params: { business_unit_id: businessUnitId } });
    setSummary(res.data || null);
  }, [businessUnitId]);

  const loadRows = useCallback(async () => {
    const request = ++rowRequest.current;
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/pos/sales/rows", {
        params: {
          business_unit_id: businessUnitId,
          page,
          page_size: pageSize,
          q: debouncedSearchQuery,
          filters: JSON.stringify(columnFilters),
          view: showOriginalSales ? "original" : "current",
        },
      });
      if (request !== rowRequest.current) return;
      setRows(res.data?.rows || []);
      setTotalRows(Number(res.data?.total || 0));
    } catch (err) {
      if (request === rowRequest.current) setError(err?.response?.data?.error || "Could not load POS sales.");
    } finally {
      if (request === rowRequest.current) setLoading(false);
    }
  }, [businessUnitId, columnFilters, debouncedSearchQuery, page, pageSize, showOriginalSales]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadRows(), loadSummary().catch(() => {})]);
  }, [loadRows, loadSummary]);

  const loadPaymentMethods = useCallback(async () => {
    const res = await axios.get("/api/pos/payment-methods", {
      params: {
        business_unit_id: businessUnitId,
        limit: 250,
        offset: 0,
      },
    });
    setPaymentMethods(res.data?.rows || []);
  }, [businessUnitId]);

  const loadProductOptions = useCallback(async () => {
    const res = await axios.get("/api/pos/products/options", {
      params: {
        business_unit_id: businessUnitId,
        limit: 1000,
      },
    });
    setProductOptions(res.data?.rows || []);
  }, [businessUnitId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [businessUnitId, debouncedSearchQuery, columnFilters, pageSize, showOriginalSales]);

  useEffect(() => {
    reloadAll();
  }, [reloadAll, manualReload]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!activeFilter) return;
      if (filterPopoverRef.current?.contains(event.target)) return;
      if (event.target?.closest?.("[data-pos-filter-trigger]")) return;
      setActiveFilter(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [activeFilter]);

  const columnIsFiltered = (key) => Boolean(String(columnFilters[key] || "").trim());

  const toggleFilterPopover = (column, target) => {
    const rect = target.getBoundingClientRect();
    setActiveFilter((current) => {
      if (current?.key === column.key) return null;
      setFilterDraft(columnFilters[column.key] || "");
      return {
        key: column.key,
        label: column.label,
        rect: {
          left: rect.left,
          top: rect.top,
          bottom: rect.bottom,
          width: rect.width,
        },
      };
    });
  };

  const filterPopoverStyle = useMemo(() => {
    if (!activeFilter?.rect) return {};
    const pad = 12;
    const width = Math.min(380, window.innerWidth - pad * 2);
    let left = activeFilter.rect.left;
    left = Math.max(pad, Math.min(left, window.innerWidth - width - pad));
    const top = Math.min(activeFilter.rect.bottom + 10, window.innerHeight - 240);
    return { top: Math.max(pad, top), left, width };
  }, [activeFilter]);

  const applyColumnFilter = () => {
    if (!activeFilter) return;
    setColumnFilters((prev) => ({
      ...prev,
      [activeFilter.key]: filterDraft.trim(),
    }));
    setActiveFilter(null);
  };

  const clearColumnFilter = () => {
    if (!activeFilter) return;
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[activeFilter.key];
      return next;
    });
    setFilterDraft("");
    setActiveFilter(null);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setColumnFilters({});
    setActiveFilter(null);
  };

  const openTicketEditor = async (row) => {
    const checkId = row?.pos_check_id;
    if (!checkId) return;
    try {
      setEditingCheckId(checkId);
      setTicket(null);
      setTicketDraft(null);
      setTicketError("");
      setTicketMode("edit");
      setTicketLoading(true);
      const res = await axios.get(`/api/pos/checks/${checkId}`, {
        params: { business_unit_id: businessUnitId },
      });
      setTicket(res.data || null);
      setTicketDraft(buildTicketDraft(res.data || {}));
      loadPaymentMethods().catch(() => {});
      loadProductOptions().catch(() => {});
    } catch (err) {
      setTicketError(err?.response?.data?.error || "Could not load ticket.");
    } finally {
      setTicketLoading(false);
    }
  };

  const closeTicketEditor = () => {
    setEditingCheckId(null);
    setTicket(null);
    setTicketDraft(null);
    setTicketError("");
    setTicketMode("edit");
    setTicketLoading(false);
    setTicketSaving(false);
  };

  const updateTicketCheckField = (field, value) => {
    setTicketDraft((current) => ({
      ...current,
      check: {
        ...(current?.check || {}),
        [field]: value,
      },
    }));
  };

  const updateTicketLine = (localId, field, value) => {
    setTicketDraft((current) => ({
      ...current,
      lines: (current?.lines || []).map((line) => (line.local_id === localId ? { ...line, [field]: value } : line)),
    }));
  };

  const updateTicketProduct = (localId, productId) => {
    const product = productOptions.find((row) => String(row.id) === String(productId));
    setTicketDraft((current) => ({
      ...current,
      lines: (current?.lines || []).map((line) => {
        if (line.local_id !== localId) return line;
        if (!product) {
          return {
            ...line,
            pos_product_id: "",
            item_name: "",
            plu: "",
          };
        }
        return {
          ...line,
          pos_product_id: String(product.id),
          item_name: product.name,
          plu: product.plu,
        };
      }),
    }));
  };

  const addTicketLine = () => {
    setTicketDraft((current) => ({
      ...current,
      lines: [...(current?.lines || []), buildBlankTicketLine(ticket?.check || {})],
    }));
  };

  const removeTicketLine = (localId) => {
    setTicketDraft((current) => ({
      ...current,
      lines: (current?.lines || []).reduce((acc, line) => {
        if (line.local_id !== localId) return [...acc, line];
        if (!line.id) return acc;
        return [...acc, { ...line, _delete: !line._delete }];
      }, []),
    }));
  };

  const updateTicketPayment = (localId, field, value) => {
    setTicketDraft((current) => ({
      ...current,
      payments: (current?.payments || []).map((payment) => {
        if (payment.local_id !== localId) return payment;
        if (field !== "payment_method") return { ...payment, [field]: value };
        const method = paymentMethods.find((row) => row.name === value);
        return {
          ...payment,
          payment_method: value,
          pos_payment_method_id: method?.id || null,
          category: method?.category || "other",
          category_label: method?.category_label || categoryLabel(method?.category),
        };
      }),
    }));
  };

  const addTicketPayment = () => {
    setTicketDraft((current) => ({
      ...current,
      payments: [...(current?.payments || []), buildBlankTicketPayment()],
    }));
  };

  const removeTicketPayment = (localId) => {
    setTicketDraft((current) => ({
      ...current,
      payments: (current?.payments || []).reduce((acc, payment) => {
        if (payment.local_id !== localId) return [...acc, payment];
        if (!payment.id) return acc;
        return [...acc, { ...payment, _delete: !payment._delete }];
      }, []),
    }));
  };

  const originalTicketDraft = useMemo(
    () => (ticket?.source_snapshot ? buildTicketDraft(ticket.source_snapshot) : null),
    [ticket]
  );
  const displayedTicketDraft = ticketMode === "original" ? originalTicketDraft : ticketDraft;
  const ticketTotals = useMemo(() => computeDraftTotals(displayedTicketDraft), [displayedTicketDraft]);
  const paymentDifference = ticketTotals.total - ticketTotals.paymentTotal;
  const paymentMismatch = Math.abs(paymentDifference) > 0.01;
  const ticketDifferences = useMemo(
    () => buildTicketDifferences(ticket?.source_snapshot, ticket),
    [ticket]
  );
  const ticketIsModified = Boolean(ticket?.check?.is_manually_modified);
  const ticketReadOnly = ticketMode !== "edit";

  const saveTicket = async () => {
    if (!editingCheckId || !ticketDraft) return;
    const lineWithoutProduct = (ticketDraft.lines || []).some((line) => !line._delete && !line.pos_product_id);
    if (lineWithoutProduct) {
      setTicketError("Every active ticket line needs a product from the POS catalog.");
      return;
    }

    try {
      setTicketSaving(true);
      setTicketError("");
      const res = await axios.patch(`/api/pos/checks/${editingCheckId}`, {
        business_unit_id: businessUnitId,
        check: ticketDraft.check,
        lines: ticketDraft.lines,
        payments: ticketDraft.payments,
        note: ticketDraft.note,
      });
      setTicket(res.data || null);
      setTicketDraft(buildTicketDraft(res.data || {}));
      setTicketMode("edit");
      await reloadAll();
    } catch (err) {
      setTicketError(err?.response?.data?.error || "Could not save ticket.");
    } finally {
      setTicketSaving(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      setError("");
      const response = await axios.get("/api/pos/imports/template", { responseType: "blob" });
      const contentType =
        response.headers?.["content-type"] ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const fileName = parseFileNameFromDisposition(response.headers?.["content-disposition"]);
      const blob = new Blob([response.data], { type: contentType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not download POS template.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("business_unit_id", businessUnitId);

    try {
      setUploading(true);
      setError("");
      setLastImportMessage("");
      const res = await axios.post("/api/pos/imports/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const rawRows = res.data?.summary?.raw_rows_count || res.data?.batch?.raw_rows_count || 0;
      const outletsCreated = Number(res.data?.summary?.outlets_created_count || 0);
      const productsCreated = Number(res.data?.summary?.products_created_count || 0);
      setLastImportMessage(
        res.data?.duplicate
          ? "This Excel was already imported."
          : `Import completed: ${Number(rawRows).toLocaleString("es-ES")} rows · ${outletsCreated.toLocaleString("es-ES")} new outlets · ${productsCreated.toLocaleString("es-ES")} new products.`
      );
      await reloadAll();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not import POS Excel.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="reservations-container rm-page posSalesPage">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">POS sales</h3>
            <div className="rm-rates-subtitle">
              {summary?.last_import?.date_from || "-"} / {summary?.last_import?.date_to || "-"} · {formatMoney(summary?.total_sales)}
            </div>
          </div>

          <div className="rm-topActions">
            {canCreateManualSale(businessUnitId) && <button type="button" className="rm-topBtn rm-topBtn--green" onClick={() => setManualSaleOpen(true)}><FiPlus /> {manualSpanish ? "Venta manual" : "Manual sale"}</button>}
            <input
              className="rm-searchInput"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search in sales"
            />
            <button
              type="button"
              className={`rm-topBtn posOriginalToggle ${showOriginalSales ? "posOriginalToggle--active" : ""}`}
              onClick={() => setShowOriginalSales((current) => !current)}
            >
              <FiEye /> {showOriginalSales ? "Current" : "Show Original"}
            </button>
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={clearAllFilters}>
              <FiX /> Clear
            </button>
            <button
              type="button"
              className="rm-topBtn rm-topBtn--green"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <FiUploadCloud /> {uploading ? "Importing" : "Import POS Excel"}
            </button>
            <button
              type="button"
              className="rm-topBtn rm-topBtn--cyan"
              onClick={downloadTemplate}
              disabled={downloadingTemplate}
            >
              <FiDownload /> {downloadingTemplate ? "Downloading" : "Download template"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={(event) => uploadFile(event.target.files?.[0])}
            />
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {lastImportMessage ? <div className="posInfoBanner">{lastImportMessage}</div> : null}
      {createdSale && <div className="posInfoBanner" role="status">
        {manualSpanish ? "Venta guardada" : "Sale saved"}: {createdSale.external_check_id} · Total {Number(createdSale.check_total).toLocaleString(manualSpanish ? "es-ES" : "en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}{" "}
        <button type="button" className="rm-topBtn" onClick={() => openTicketEditor({ pos_check_id: createdSale.id })}>{manualSpanish ? "Ver ticket creado" : "View created ticket"}</button>
      </div>}
      {manualSaleOpen && <ManualSaleDialog businessUnitId={businessUnitId} onClose={() => setManualSaleOpen(false)} onCreated={(check) => {
        rowRequest.current += 1;
        setManualSaleOpen(false); setCreatedSale(check); clearAllFilters(); setShowOriginalSales(false); setPage(1); setManualReload((value) => value + 1);
      }} />}


      <div className="rm-rates-card">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">Sales detail</h3>
          <div className="rm-rates-subtitle">
            {showOriginalSales
              ? "Original imported POS rows, before manual edits"
              : "Current ticket lines with the original import kept as source"}
          </div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading sales..." : `Showing ${pageStart}-${pageEnd} of ${totalRows}`}
          </small>
          <div className="posPager">
            <label htmlFor="pos-sales-page-size" className="res-listMetaText">Size</label>
            <select
              id="pos-sales-page-size"
              className="posPageSizeSelect"
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value) || 100)}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <button type="button" className="rm-topBtn" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
              Previous
            </button>
            <small className="res-listMetaText">Page {page} / {totalPages}</small>
            <button type="button" className="rm-topBtn" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>
              Next
            </button>
          </div>
        </div>

        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table posSalesTable" style={{ "--rm-col1-width": "170px", "--rm-table-min-width": "2780px" }}>
            <colgroup>
              {POS_SALES_COLUMNS.map((column) => (
                <col key={column.key} style={{ width: `${column.width}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {POS_SALES_COLUMNS.map((column, index) => {
                  const stickyClass = index === 0 ? "rm-sticky-1" : index === 1 ? "rm-sticky-2" : "";
                  return (
                    <th key={column.key} className={`res-th ${stickyClass}`}>
                      <button
                        type="button"
                        data-pos-filter-trigger={column.key}
                        className={`res-thBtn ${columnIsFiltered(column.key) ? "res-thBtn--filtered" : ""} ${
                          activeFilter?.key === column.key ? "res-thBtn--open" : ""
                        }`}
                        onClick={(event) => toggleFilterPopover(column, event.currentTarget)}
                      >
                        <span className="res-thLabel">{column.label}</span>
                        <span className="res-thSub">{columnFilters[column.key] || column.sub}</span>
                        <span className={`res-thDot ${columnIsFiltered(column.key) ? "res-thDot--on" : ""}`} aria-hidden="true" />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={`${showOriginalSales ? "posOriginalSalesRow" : ""} ${
                    row.is_removed ? "posOriginalSalesRow--removed" : ""
                  }`}
                >
                  {POS_SALES_COLUMNS.map((column, index) => {
                    const stickyClass = index === 0 ? "rm-sticky-1" : index === 1 ? "rm-sticky-2" : "";
                    return (
                      <td key={column.key} className={stickyClass}>
                        {column.key === "check_number" ? (
                          <div className="posTicketCell">
                            <button type="button" className="posEditTicketBtn" onClick={() => openTicketEditor(row)}>
                              <FiEdit3 /> {displayValue(row, column, showOriginalSales)}
                            </button>
                            {row.is_manually_modified ? <span className="rm-chip rm-chip--soft">Modified</span> : null}
                            {showOriginalSales && row.is_removed ? <span className="rm-chip rm-chip--off">Removed</span> : null}
                          </div>
                        ) : (
                          displayValue(row, column, showOriginalSales)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={POS_SALES_COLUMNS.length} className="posMuted">
                    No POS sales found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {activeFilter ? (
        <div ref={filterPopoverRef} className="res-filterPopover" style={filterPopoverStyle}>
          <div className="res-filterHeader">
            <div className="res-filterTitle">{activeFilter.label}</div>
            <div className="res-filterHeaderActions">
              <button type="button" className="res-filterSmallBtn" onClick={clearColumnFilter}>Clear</button>
              <button type="button" className="res-filterCloseBtn" onClick={() => setActiveFilter(null)} aria-label="Close filter">
                x
              </button>
            </div>
          </div>
          <div className="res-filterBody">
            <input
              className="res-filterInput"
              value={filterDraft}
              onChange={(event) => setFilterDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") applyColumnFilter();
                if (event.key === "Escape") setActiveFilter(null);
              }}
              placeholder="Filter this column"
              autoFocus
            />
            <div className="res-filterHint">Matches any text contained in this column.</div>
            <div className="posFilterActions">
              <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={applyColumnFilter}>
                Apply filter
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editingCheckId
        ? createPortal(
            <div className="posModalOverlay">
              <div className="posTicketModal" role="dialog" aria-modal="true" aria-label="Edit POS ticket">
                <div className="posTicketModalHeader">
                  <div>
                    <h3 className="rm-rates-title">
                      {ticketMode === "original" ? "Original ticket" : ticketMode === "diff" ? "Ticket differences" : "Edit ticket"}{" "}
                      {ticket?.check?.external_check_id || editingCheckId}
                    </h3>
                    <div className="rm-rates-subtitle">
                      {ticket?.check?.outlet_name || "-"} / {ticket?.check?.check_date || "-"}
                      {ticket?.check?.is_manually_modified ? " / manually modified" : ""}
                    </div>
                  </div>
                  <div className="posTicketModeGroup">
                    <button
                      type="button"
                      className={`posTicketModeBtn ${ticketMode === "edit" ? "posTicketModeBtn--active" : ""}`}
                      onClick={() => setTicketMode("edit")}
                    >
                      <FiEdit3 /> Current
                    </button>
                    {ticketIsModified ? (
                      <button
                        type="button"
                        className={`posTicketModeBtn ${ticketMode === "original" ? "posTicketModeBtn--active" : ""}`}
                        onClick={() => setTicketMode("original")}
                      >
                        <FiEye /> Original
                      </button>
                    ) : null}
                    {ticketIsModified ? (
                      <button
                        type="button"
                        className={`posTicketModeBtn ${ticketMode === "diff" ? "posTicketModeBtn--active" : ""}`}
                        onClick={() => setTicketMode("diff")}
                      >
                        <FiGitPullRequest /> Differences
                      </button>
                    ) : null}
                  </div>
                  <button type="button" className="res-filterCloseBtn" onClick={closeTicketEditor} aria-label="Close ticket">
                    x
                  </button>
                </div>

                {ticketError ? <div className="posStateBanner">{ticketError}</div> : null}
                {ticketLoading ? <div className="posInfoBanner">Loading ticket...</div> : null}

                {!ticketLoading && ticketMode === "diff" ? (
                  <div className="posTicketDiffView">
                    {ticketDifferences.length ? (
                      ticketDifferences.map((section, index) => (
                        <div key={`${section.type}-${index}`} className={`posTicketDiffCard posTicketDiffCard--${section.type}`}>
                          <div className="posTicketDiffTitle">{section.title}</div>
                          <div className="posTicketDiffRows">
                            {section.changes.map((change) => (
                              <div key={`${section.title}-${change.label}`} className="posTicketDiffRow">
                                <span>{change.label}</span>
                                <strong>{formatDiffValue(change.before)}</strong>
                                <FiGitPullRequest />
                                <strong>{formatDiffValue(change.after)}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="posInfoBanner">No differences found against the original source.</div>
                    )}
                  </div>
                ) : null}

                {!ticketLoading && ticketMode !== "diff" && displayedTicketDraft ? (
                  <>
                    <datalist id="pos-payment-method-options">
                      {paymentMethods.map((method) => (
                        <option key={method.id} value={method.name} />
                      ))}
                    </datalist>

                    <div className="posTicketSummaryGrid">
                      <label className="posTicketField">
                        <span>Pax / Covers</span>
                        <input
                          className="posTextInput"
                          value={displayedTicketDraft.check.guests}
                          disabled={ticketReadOnly}
                          onChange={(event) => updateTicketCheckField("guests", event.target.value)}
                        />
                        <small>{ticketReadOnly ? "Original source" : `Source: ${getSourceValue(ticket, "guests")}`}</small>
                      </label>
                      <label className="posTicketField">
                        <span>Table</span>
                        <input
                          className="posTextInput"
                          value={displayedTicketDraft.check.table_number}
                          disabled={ticketReadOnly}
                          onChange={(event) => updateTicketCheckField("table_number", event.target.value)}
                        />
                        <small>{ticketReadOnly ? "Original source" : `Source: ${getSourceValue(ticket, "table_number")}`}</small>
                      </label>
                      <label className="posTicketField">
                        <span>Room</span>
                        <input
                          className="posTextInput"
                          value={displayedTicketDraft.check.room_number}
                          disabled={ticketReadOnly}
                          onChange={(event) => updateTicketCheckField("room_number", event.target.value)}
                        />
                        <small>{ticketReadOnly ? "Original source" : `Source: ${getSourceValue(ticket, "room_number")}`}</small>
                      </label>
                      <label className="posTicketField">
                        <span>Revenue type</span>
                        <input
                          className="posTextInput"
                          value={displayedTicketDraft.check.revenue_type}
                          disabled={ticketReadOnly}
                          onChange={(event) => updateTicketCheckField("revenue_type", event.target.value)}
                        />
                        <small>{ticketReadOnly ? "Original source" : `Source: ${getSourceValue(ticket, "revenue_type")}`}</small>
                      </label>
                      <label className="posTicketField">
                        <span>Cashier</span>
                        <input
                          className="posTextInput"
                          value={displayedTicketDraft.check.cashier_name}
                          disabled={ticketReadOnly}
                          onChange={(event) => updateTicketCheckField("cashier_name", event.target.value)}
                        />
                        <small>{ticketReadOnly ? "Original source" : `Source: ${getSourceValue(ticket, "cashier_name")}`}</small>
                      </label>
                      <label className="posTicketField">
                        <span>Dining time</span>
                        <input
                          className="posTextInput"
                          value={displayedTicketDraft.check.dining_time}
                          disabled={ticketReadOnly}
                          onChange={(event) => updateTicketCheckField("dining_time", event.target.value)}
                        />
                        <small>{ticketReadOnly ? "Original source" : `Source: ${getSourceValue(ticket, "dining_time")}`}</small>
                      </label>
                      <label className="posTicketField">
                        <span>Status</span>
                        <input
                          className="posTextInput"
                          value={displayedTicketDraft.check.status}
                          disabled={ticketReadOnly}
                          onChange={(event) => updateTicketCheckField("status", event.target.value)}
                        />
                        <small>{ticketReadOnly ? "Original source" : `Source: ${getSourceValue(ticket, "status")}`}</small>
                      </label>
                    </div>

                    <div className="posTicketLinesHeader">
                      <div>
                        <h3 className="rm-rates-title">Ticket lines</h3>
                        <div className="rm-rates-subtitle">
                          {ticketReadOnly
                            ? "Original imported values, read only"
                            : "Edit current values; source values stay below each line."}
                        </div>
                      </div>
                      {!ticketReadOnly ? (
                        <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={addTicketLine}>
                          <FiPlus /> Add line
                        </button>
                      ) : null}
                    </div>

                    <div className="posTicketLinesWrap">
                      <table className="rm-rates-table posTicketLinesTable">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>PLU</th>
                            <th>Time</th>
                            <th>Qty</th>
                            <th>Amount</th>
                            <th>SC</th>
                            <th>Discount type</th>
                            <th>Discount</th>
                            <th>Operator</th>
                            <th>Source</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {displayedTicketDraft.lines.map((line) => (
                            <tr key={line.local_id} className={line._delete ? "posTicketLineRemoved" : ""}>
                              <td>
                                {ticketReadOnly ? (
                                  <input
                                    className="posTextInput posTicketLineInput"
                                    value={line.item_name}
                                    disabled
                                  />
                                ) : (
                                  <select
                                    className="posTableSelect posTicketProductSelect"
                                    value={line.pos_product_id || ""}
                                    disabled={line._delete}
                                    onChange={(event) => updateTicketProduct(line.local_id, event.target.value)}
                                  >
                                    <option value="">Select product</option>
                                    {line.pos_product_id &&
                                    !productOptions.some((product) => String(product.id) === String(line.pos_product_id)) ? (
                                      <option value={line.pos_product_id}>
                                        {productOptionLabel({ plu: line.plu, name: line.item_name })}
                                      </option>
                                    ) : null}
                                    {productOptions.map((product) => (
                                      <option key={product.id} value={product.id}>
                                        {productOptionLabel(product)}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </td>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  value={line.plu}
                                  disabled
                                />
                              </td>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  value={line.line_time}
                                  disabled={ticketReadOnly || line._delete}
                                  onChange={(event) => updateTicketLine(line.local_id, "line_time", event.target.value)}
                                />
                              </td>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  value={line.quantity}
                                  disabled={ticketReadOnly || line._delete}
                                  onChange={(event) => updateTicketLine(line.local_id, "quantity", event.target.value)}
                                />
                              </td>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  value={line.line_amount}
                                  disabled={ticketReadOnly || line._delete}
                                  onChange={(event) => updateTicketLine(line.local_id, "line_amount", event.target.value)}
                                />
                              </td>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  value={line.service_charge_amount}
                                  disabled={ticketReadOnly || line._delete}
                                  onChange={(event) =>
                                    updateTicketLine(line.local_id, "service_charge_amount", event.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  value={line.discount_type}
                                  disabled={ticketReadOnly || line._delete}
                                  onChange={(event) =>
                                    updateTicketLine(line.local_id, "discount_type", event.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  value={line.discount_amount}
                                  disabled={ticketReadOnly || line._delete}
                                  onChange={(event) =>
                                    updateTicketLine(line.local_id, "discount_amount", event.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  value={line.operator_name}
                                  disabled={ticketReadOnly || line._delete}
                                  onChange={(event) =>
                                    updateTicketLine(line.local_id, "operator_name", event.target.value)
                                  }
                                />
                              </td>
                              <td className="posTicketSourceCell">
                                <span>{getSourceLineLabel(line)}</span>
                                <small>{line.edit_status}</small>
                              </td>
                              <td>
                                {!ticketReadOnly ? (
                                  <button
                                    type="button"
                                    className={`posTicketIconBtn ${line._delete ? "posTicketIconBtn--restore" : ""}`}
                                    onClick={() => removeTicketLine(line.local_id)}
                                    title={line._delete ? "Restore line" : "Remove line"}
                                  >
                                    {line._delete ? <FiPlus /> : <FiTrash2 />}
                                  </button>
                                ) : (
                                  <span className="rm-chip rm-chip--soft">Original</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="posTicketLinesHeader posTicketPaymentsHeader">
                      <div>
                        <h3 className="rm-rates-title">Payments</h3>
                        <div className="rm-rates-subtitle">
                          {ticketReadOnly ? "Original imported tender values, read only" : "Edit the current tender values for this ticket."}
                        </div>
                      </div>
                      {!ticketReadOnly ? (
                        <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={addTicketPayment}>
                          <FiPlus /> Add payment
                        </button>
                      ) : null}
                    </div>

                    <div className="posTicketLinesWrap posTicketPaymentsWrap">
                      <table className="rm-rates-table posTicketLinesTable posTicketPaymentsTable">
                        <thead>
                          <tr>
                            <th>Method</th>
                            <th>Amount</th>
                            <th>Category</th>
                            <th>Source</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {displayedTicketDraft.payments.map((payment) => (
                            <tr key={payment.local_id} className={payment._delete ? "posTicketLineRemoved" : ""}>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  list="pos-payment-method-options"
                                  value={payment.payment_method}
                                  disabled={ticketReadOnly || payment._delete}
                                  onChange={(event) =>
                                    updateTicketPayment(payment.local_id, "payment_method", event.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="posTextInput posTicketLineInput"
                                  value={payment.amount}
                                  disabled={ticketReadOnly || payment._delete}
                                  onChange={(event) => updateTicketPayment(payment.local_id, "amount", event.target.value)}
                                />
                              </td>
                              <td>
                                <span className="rm-chip rm-chip--soft">{payment.category_label || categoryLabel(payment.category)}</span>
                              </td>
                              <td className="posTicketSourceCell">
                                <span>{getSourcePaymentLabel(payment)}</span>
                                <small>{payment.id ? "ticket payment" : "manual payment"}</small>
                              </td>
                              <td>
                                {!ticketReadOnly ? (
                                  <button
                                    type="button"
                                    className={`posTicketIconBtn ${payment._delete ? "posTicketIconBtn--restore" : ""}`}
                                    onClick={() => removeTicketPayment(payment.local_id)}
                                    title={payment._delete ? "Restore payment" : "Remove payment"}
                                  >
                                    {payment._delete ? <FiPlus /> : <FiTrash2 />}
                                  </button>
                                ) : (
                                  <span className="rm-chip rm-chip--soft">Original</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {!displayedTicketDraft.payments.length ? (
                            <tr>
                              <td colSpan="5" className="posMuted">No payments on this ticket.</td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>

                    <div className="posTicketFooter">
                      {!ticketReadOnly ? (
                        <label className="posTicketNoteField">
                          <span>Edit note</span>
                          <textarea
                            className="posTextInput"
                            value={ticketDraft.note}
                            onChange={(event) =>
                              setTicketDraft((current) => ({ ...current, note: event.target.value }))
                            }
                            placeholder="Optional reason for the manual change"
                          />
                        </label>
                      ) : (
                        <div className="posTicketReadOnlyHint">
                          <strong>Original source</strong>
                          <span>This is the imported ticket before manual edits.</span>
                        </div>
                      )}

                      <div className={`posTicketTotals ${paymentMismatch ? "posTicketTotals--warning" : ""}`}>
                        <span>{ticketTotals.lines} active lines / {ticketTotals.payments} payments</span>
                        <strong>{formatMoney(ticketTotals.total)}</strong>
                        <small>
                          Lines {formatMoney(ticketTotals.lineTotal)} / SC {formatMoney(ticketTotals.service)} / Discounts{" "}
                          {formatMoney(ticketTotals.discount)}
                        </small>
                        <small>
                          Payments {formatMoney(ticketTotals.paymentTotal)} / Difference {formatMoney(paymentDifference)}
                        </small>
                      </div>

                      <div className="posTicketActions">
                        {ticketReadOnly ? (
                          <button type="button" className="rm-topBtn" onClick={() => setTicketMode("edit")}>
                            <FiEdit3 /> Current
                          </button>
                        ) : null}
                        <button type="button" className="rm-topBtn" onClick={closeTicketEditor}>
                          <FiX /> Cancel
                        </button>
                        {!ticketReadOnly ? (
                          <button
                            type="button"
                            className="rm-topBtn rm-topBtn--green"
                            onClick={saveTicket}
                            disabled={ticketSaving}
                          >
                            <FiSave /> {ticketSaving ? "Saving" : "Save ticket"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
